import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { 
    ChevronLeft, RefreshCw, Users, Award, Grid, Building, Calendar, Download, X
} from "lucide-react";
import { 
    fetchConsolidationCampagneRequest
} from "../../../redux/actions/performanceActions";
import { fetchCampagnesRequest } from "../../../redux/actions/campagnesActions";
import { performanceApi } from "../../../redux/api/performanceApi";
import PerformanceConsolidationKpis from "./PerformanceConsolidationKpis";
import PerformanceCollaborateursTable from "./PerformanceCollaborateursTable";
import PerformanceManagersTable from "./PerformanceManagersTable";
import PerformanceEntitiesTable from "./PerformanceEntitiesTable";
import PerformanceMissingDataTable from "./PerformanceMissingDataTable";
import PerformanceProgressDetailModal from "./PerformanceProgressDetailModal";
import "./PerformanceConsolidation.css";

const ConsolidationActionModal = ({ open, title, children, footer, onClose }) => {
    if (!open) return null;

    return (
        <div className="perf-cons-action-modal" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
            <div className="perf-cons-action-panel">
                <div className="perf-cons-action-header">
                    <h3>{title}</h3>
                    <button className="perf-cons-action-close" type="button" onClick={onClose} title="Fermer">
                        <X size={22} />
                    </button>
                </div>
                <div className="perf-cons-action-body">{children}</div>
                <div className="perf-cons-action-footer">{footer}</div>
            </div>
        </div>
    );
};

export default function PerformanceConsolidationDashboard() {
    const { campagneId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const consolidationState = useSelector((s) => s.performance?.consolidation || {});
    const data = consolidationState.byCampagneId?.[String(campagneId)];
    const loading = consolidationState.loadingByCampagneId?.[String(campagneId)];
    const error = consolidationState.errorByCampagneId?.[String(campagneId)];

    const campaigns = useSelector((s) => s.campagnes?.list || []);
    const selectedCampaign = campaigns.find((c) => String(c.id) === String(campagneId));

    const [activeTab, setActiveTab] = useState("collaborateurs"); // collaborateurs, managers, entities, alerts
    const [selectedProgressId, setSelectedProgressId] = useState(null);
    const [openProlonger, setOpenProlonger] = useState(false);
    const [openRelancer, setOpenRelancer] = useState(false);
    const [newEndDate, setNewEndDate] = useState("");
    const [prolongerComment, setProlongerComment] = useState("");
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        if (campagneId) {
            dispatch(fetchConsolidationCampagneRequest(campagneId));
        }
    }, [dispatch, campagneId]);

    const formatDate = (date) => {
        if (!date) return "—";
        const d = new Date(date);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("fr-FR");
    };

    const calculateParticipation = () => {
        const total = data?.totalCollaborateurs || 0;
        const nonDemarres = data?.notStartedCount || 0;
        if (total === 0) return "0%";
        const rate = ((total - nonDemarres) / total) * 100;
        if (rate % 1 === 0) {
            return `${rate}%`;
        }
        return `${rate.toFixed(2).replace(".", ",")}%`;
    };

    const handleOpenDetail = (progressId) => {
        setSelectedProgressId(progressId);
    };

    const handleBack = () => {
        navigate("/performance/rh/consolidation");
    };

    const resetProlongerForm = () => {
        setNewEndDate("");
        setProlongerComment("");
    };

    const handleSubmitProlonger = async () => {
        if (!campagneId || !newEndDate) {
            Swal.fire({
                icon: "warning",
                text: "Veuillez saisir la nouvelle date de fin.",
                timer: 2200,
                showConfirmButton: false,
            });
            return;
        }

        setActionLoading(true);
        try {
            await performanceApi.prolongerCampagne(campagneId, {
                newEndDate,
                comment: prolongerComment,
            });
            setOpenProlonger(false);
            resetProlongerForm();
            dispatch(fetchCampagnesRequest());
            dispatch(fetchConsolidationCampagneRequest(campagneId));
            Swal.fire({
                icon: "success",
                text: "Campagne prolongée avec succès.",
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire({
                icon: "error",
                text: err?.response?.data?.message || "Erreur lors de la prolongation de la campagne.",
                timer: 2600,
                showConfirmButton: false,
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleSubmitRelancer = () => {
        setOpenRelancer(false);
        Swal.fire({
            icon: "success",
            text: "Relance préparée pour les collaborateurs concernés.",
            timer: 1800,
            showConfirmButton: false,
        });
    };

    const tabConfigs = [
        { id: "collaborateurs", label: "Collaborateurs", icon: Users, badge: data?.collaborateurs?.length || 0 },
        { id: "managers", label: "Synthèse Managers", icon: Award, badge: data?.managers?.length || 0 },
        { id: "entities", label: "Synthèse Entités", icon: Grid, badge: data?.entities?.length || 0 },
        // { id: "alerts", label: "Alertes & Données Manquantes", badge: data?.missingData?.length || 0, isAlert: true }
    ];

    return (
        <div className="perf-cons-container">
            {/* Back Button */}
            <button className="perf-cons-back-btn" onClick={handleBack}>
                <ChevronLeft size={16} /> Retour aux campagnes
            </button>

            {/* Header Toolbar - Bloc Campagne */}
            <div className="perf-campaign-banner">
                <div className="perf-campaign-left">
                    <div className="perf-campaign-icon-box">
                        <Building size={20} />
                    </div>
                    
                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Campagne</span>
                        <span className="perf-campaign-value highlighted-name">
                            {selectedCampaign?.nom || data?.campagneNom || `Campagne #${campagneId}`}
                        </span>
                    </div>

                    <div className="perf-campaign-divider" />

                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Début</span>
                        <span className="perf-campaign-value">
                            <Calendar size={14} className="perf-campaign-date-icon" />
                            {formatDate(selectedCampaign?.dateDebut)}
                        </span>
                    </div>

                    <div className="perf-campaign-divider" />

                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Fin</span>
                        <span className="perf-campaign-value">
                            <Calendar size={14} className="perf-campaign-date-icon-fin" />
                            {formatDate(selectedCampaign?.dateFin)}
                        </span>
                    </div>

                    <div className="perf-campaign-divider" />

                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Collaborateurs</span>
                        <span className="perf-campaign-value highlighted-collabs">
                            {selectedCampaign?.filterMode === "IN" && selectedCampaign?.userIds?.length 
                                ? "Collaborateurs ciblés" 
                                : "Tous les collaborateurs"}
                        </span>
                    </div>

                    <div className="perf-campaign-divider" />

                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Participation</span>
                        <span className="perf-campaign-value highlighted-participation">
                            {calculateParticipation()}
                        </span>
                    </div>
                </div>

                <div className="perf-campaign-actions">
                    <button className="perf-banner-btn perf-banner-btn--extract">
                        <Download size={14} /> Extraire le Bilan
                    </button>
                    <button className="perf-banner-btn perf-banner-btn--prolong" type="button" onClick={() => setOpenProlonger(true)}>
                        <Calendar size={14} /> Prolonger
                    </button>
                    <button className="perf-banner-btn perf-banner-btn--relaunch" type="button" onClick={() => setOpenRelancer(true)}>
                        <RefreshCw size={14} /> Relancer
                    </button>
                </div>
            </div>

            {/* Error States */}
            {error && (
                <div className="perf-state perf-state--error">
                    {error}
                </div>
            )}

            {/* Loading skeleton or overlay */}
            {loading && !data && (
                <div className="perf-state perf-state--inline" style={{ textAlign: "center", padding: "60px" }}>
                    <RefreshCw size={24} className="animate-spin" style={{ margin: "0 auto 12px", color: "#204385" }} />
                    Chargement de la consolidation de la campagne...
                </div>
            )}

            {!loading && !error && !data && (
                <div className="perf-state perf-state--warning">
                    Aucune donnée de consolidation disponible pour cette campagne de performance.
                </div>
            )}

            {/* KPI Cards & Tabs Grid */}
            {data && (
                <>
                    {/* KPIs Panel */}
                    <PerformanceConsolidationKpis kpis={data} />

                    {/* Tab Navigation */}
                    <div className="perf-cons-tabs">
                        {tabConfigs.map(tab => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    className={`perf-cons-tab-btn ${activeTab === tab.id ? "active" : ""} ${tab.isAlert ? "alert-tab" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <IconComponent size={15} />
                                    <span>{tab.label}</span>
                                    <span className="perf-cons-tab-badge">{tab.badge}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Tab content panel */}
                    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)" }}>
                        {activeTab === "collaborateurs" && (
                            <PerformanceCollaborateursTable 
                                collaborateurs={data.collaborateurs} 
                                onViewDetail={handleOpenDetail} 
                            />
                        )}

                        {activeTab === "managers" && (
                            <PerformanceManagersTable 
                                managers={data.managers} 
                            />
                        )}

                        {activeTab === "entities" && (
                            <PerformanceEntitiesTable 
                                entities={data.entities} 
                            />
                        )}

                        {activeTab === "alerts" && (
                            <PerformanceMissingDataTable 
                                missingData={data.missingData} 
                                collaborateurs={data.collaborateurs}
                                onViewDetail={handleOpenDetail} 
                            />
                        )}
                    </div>
                </>
            )}

            {/* Individual Progress Detail Modal */}
            {selectedProgressId && (
                <PerformanceProgressDetailModal 
                    progressId={selectedProgressId} 
                    onClose={() => setSelectedProgressId(null)} 
                />
            )}

            <ConsolidationActionModal
                open={openProlonger}
                title="Prolonger la campagne"
                onClose={() => {
                    setOpenProlonger(false);
                    resetProlongerForm();
                }}
                footer={
                    <>
                        <button
                            className="perf-cons-action-btn perf-cons-action-btn--secondary"
                            type="button"
                            onClick={() => {
                                setOpenProlonger(false);
                                resetProlongerForm();
                            }}
                            disabled={actionLoading}
                        >
                            Annuler
                        </button>
                        <button
                            className="perf-cons-action-btn perf-cons-action-btn--danger"
                            type="button"
                            onClick={handleSubmitProlonger}
                            disabled={actionLoading}
                        >
                            Confirmer
                        </button>
                    </>
                }
            >
                <div className="perf-cons-action-grid">
                    <label className="perf-cons-action-field">
                        <span>Nouvelle date de fin</span>
                        <input type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
                    </label>
                    <label className="perf-cons-action-field">
                        <span>Commentaire</span>
                        <input value={prolongerComment} onChange={(e) => setProlongerComment(e.target.value)} placeholder="Optionnel..." />
                    </label>
                </div>
                <div className="perf-cons-action-note">
                    Les personnes éligibles à cette campagne seront notifiées.
                </div>
            </ConsolidationActionModal>

            <ConsolidationActionModal
                open={openRelancer}
                title="Relancer"
                onClose={() => setOpenRelancer(false)}
                footer={
                    <>
                        <button
                            className="perf-cons-action-btn perf-cons-action-btn--secondary"
                            type="button"
                            onClick={() => setOpenRelancer(false)}
                        >
                            Annuler
                        </button>
                        <button
                            className="perf-cons-action-btn perf-cons-action-btn--primary"
                            type="button"
                            onClick={handleSubmitRelancer}
                        >
                            Envoyer
                        </button>
                    </>
                }
            >
                <div className="perf-cons-action-note">
                    Cette action relancera les personnes éligibles à cette campagne qui n’ont pas encore effectué d'évaluations.
                    {" "}
                    <strong>{data?.notStartedCount || 0}</strong> personne(s) concernée(s).
                </div>
            </ConsolidationActionModal>
        </div>
    );
}
